import { useEffect } from 'react';
import { useFormikContext } from 'formik';

import { ErrorColor } from 'types';
import { Icon } from 'components/icon';
import { formatPrice } from 'utils/helpers';
import {
  List,
  Flex,
  ListCard,
  Checkbox,
  ListHeader,
  InputField,
  ErrorMessage,
} from 'components';

import * as Styles from './styles';
import { FormValues, ProductsProps, HandleProductChange } from './types';

const titles = ['Product', 'Quantity', 'Price'];

export const Products = ({
  opened,
  cartData,
  isInitialize,
  setInitialize,
  editableQuantity,
}: ProductsProps) => {
  const currentCartItems = cartData?.items?.filter((item) => !item.isExchange);
  const { values, errors, setFieldValue } = useFormikContext<FormValues>();

  const handleProductChange = ({ item, checked }: HandleProductChange) => {
    const id = item.id;
    const updatedProducts = checked
      ? [
          ...values.products,
          {
            id,
            // The cart's own price (a $0 gift stays $0), never the retail price.
            price: item.price,
            name: item?.product?.name,
            productId: item.productId,
            quantity: item?.quantity || 1,
            photoUrl: item?.product?.photoUrl,
          },
        ]
      : values?.products?.filter((product) => product.id !== id);
    setFieldValue('products', updatedProducts);
  };

  useEffect(() => {
    if (!isInitialize && opened && cartData?.items) {
      // Nothing is ticked for the rep: only items already marked for this
      // shipment (e.g. a retry after a declined card) and items the booth
      // has no stock of (they can only be shipped) start ticked.
      const cartItems =
        currentCartItems
          ?.filter((item) => item.isShipping || item.isProductOutStorage)
          .map((item) => ({
            id: item.id,
            price: item.price,
            quantity: item.quantity,
            productId: item.productId,
            name: item?.product?.name,
            photoUrl: item?.product?.photoUrl,
          })) || [];

      // An empty list is already the form's start value; setting it again
      // would show "At least one product is required" before any tick.
      if (cartItems.length) {
        setFieldValue('products', cartItems);
      }
      setInitialize(true);
    }
  }, [opened, isInitialize, cartData?.items, setFieldValue, setInitialize]);

  return (
    <Flex gap={24} fullWidth flexDirection="column">
      <Styles.ShipHint
        alignCenter
        regularText
        icon={<Icon.Delivery />}
        color={ErrorColor.Yellow}
      >
        <div>
          <Styles.ShipHintTitle>
            Tick ONLY the items being shipped
          </Styles.ShipHintTitle>
          <Styles.ShipHintText>
            Items the customer takes home now stay unticked.
          </Styles.ShipHintText>
        </div>
      </Styles.ShipHint>

      {errors.products && typeof errors?.products === 'string' && (
        <ErrorMessage>{errors.products}</ErrorMessage>
      )}

      <List
        count={currentCartItems?.length}
        mainWrapperStyles={{ padding: 0, overflow: 'unset' }}
        header={<ListHeader titles={titles} padding="8px 16px" />}
      >
        {currentCartItems?.map((item) => {
          const productIndex = values?.products?.findIndex(
            (product) => product?.id === item?.id
          );
          const isChecked = productIndex !== -1;

          return (
            <ListCard
              key={item?.id}
              headers={titles}
              values={[
                <Flex gap={16}>
                  <Checkbox
                    checked={isChecked}
                    disabled={item.isProductOutStorage}
                    onChange={() =>
                      handleProductChange({ item, checked: !isChecked })
                    }
                  />
                  <p>{item?.product?.name}</p>
                </Flex>,
                // A cart line ships whole (one shipping flag per line), so its
                // quantity is fixed; a receipt's shipment can split a line.
                !isChecked ? null : editableQuantity ? (
                  <InputField
                    number
                    decimal
                    placeholder="Quantity"
                    name={`products.${productIndex}.quantity`}
                  />
                ) : (
                  <p>{item.quantity}</p>
                ),
                <p>{formatPrice(item.price)}</p>,
              ]}
            />
          );
        })}
      </List>
    </Flex>
  );
};
